import { mountToAppShell } from '../appShell'
import { NeonColors, FONTS, neonTitleGlow } from '../neon-theme'
import { NetworkManager } from '../../network/NetworkManager'
import {
  initialTonPreviewState,
  reduceTonPreview,
  type TonPreviewState,
  type TonPreviewAction,
} from './tonPreviewState'
import { formatWaitlistCountLine } from './waitlistCountLine'

/**
 * SNAKE-21: full-screen preview for the future TON real-stakes mode.
 *
 * Behaviour matches GDD:
 *   - On mount: GET /api/ton-waitlist/me → render based on wantsRealStakes
 *   - Subscribe button: POST /subscribe (optimistic switch + reconcile)
 *   - Unsubscribe: window.confirm() → POST /unsubscribe
 *   - Without auth token: show "Войдите в Telegram" message
 */

interface ApiStatusResponse {
  success: boolean
  data?: { wantsRealStakes: boolean; subscribedAt: string | null }
}

interface ApiSubscribeResponse {
  success: boolean
  data?: { wantsRealStakes: boolean; subscribedAt?: string }
}

export class TonPreviewScreen {
  private container: HTMLDivElement
  private state: TonPreviewState = initialTonPreviewState
  private onClose: (() => void) | null = null

  // DOM refs that need to be updated on every render
  private ctaBtn: HTMLButtonElement | null = null
  private statusLabel: HTMLDivElement | null = null

  // SNAKE-27: public social-proof counter. Lives outside the reducer because
  // it does not depend on subscribe state and vice-versa — keeping it separate
  // means a count refresh never disrupts the subscribe UI, and a subscribe
  // refresh never blanks the count.
  private waitlistCount: number | null = null
  private waitlistCountEl: HTMLDivElement | null = null

  // True while a network call is in-flight (prevents double-click double-submit).
  private busy = false

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'ton-preview-screen'
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      display: flex; flex-direction: column; align-items: center;
      background: rgba(13,13,26,0.96); z-index: 210;
      font-family: ${FONTS.body}; color: #fff;
      overflow-y: auto; padding: 28px 0;
    `
  }

  show(onClose: () => void): void {
    this.onClose = onClose
    this.state = initialTonPreviewState
    this.render()
    mountToAppShell(this.container)
    void this.fetchStatus()
    // SNAKE-27: count fetch is independent of auth and subscribe state.
    void this.fetchCount()
  }

  hide(): void {
    this.container.remove()
  }

  dispose(): void {
    this.hide()
    this.onClose = null
    this.waitlistCount = null
    this.waitlistCountEl = null
  }

  // -----------------------------------------------------------------------
  // Networking
  // -----------------------------------------------------------------------

  private getToken(): string | null {
    return NetworkManager.getInstance().getToken() ?? null
  }

  /**
   * SNAKE-27: fetch the public waitlist counter. No auth header (endpoint is
   * public). On success we update only the count line via applyCountToEl() —
   * never the whole screen render — so we cannot clobber the unauthenticated
   * message or the subscribe CTA mid-flight.
   */
  private async fetchCount(): Promise<void> {
    try {
      const res = await fetch('/api/ton-waitlist/count')
      if (!res.ok) return
      const data = (await res.json()) as
        | { success: boolean; data?: { count: number } }
        | null
      if (data?.success && typeof data.data?.count === 'number') {
        this.waitlistCount = data.data.count
        this.applyCountToEl()
      }
    } catch {
      // Silent — counter line just stays hidden.
    }
  }

  /**
   * Update the social-proof line in-place. The DOM ref is recreated on every
   * makeShell() call (because clear() + makeShell() runs on each render), so
   * makeShell() also calls this method on creation to restore the line from
   * the cached count. That way, fetchStatus / subscribe / unsubscribe — all
   * of which trigger render() — never lose the count.
   */
  private applyCountToEl(): void {
    if (!this.waitlistCountEl) return
    if (this.waitlistCount === null) {
      this.waitlistCountEl.style.display = 'none'
      this.waitlistCountEl.textContent = ''
      return
    }
    // SNAKE-27 follow-up: ru-RU number formatting + early-adopter copy for
    // small waitlists. See waitlistCountLine.ts for the rules.
    const line = formatWaitlistCountLine(this.waitlistCount)
    if (line === '') {
      this.waitlistCountEl.style.display = 'none'
      this.waitlistCountEl.textContent = ''
      return
    }
    this.waitlistCountEl.textContent = line
    this.waitlistCountEl.style.display = 'block'
  }

  private async fetchStatus(): Promise<void> {
    const token = this.getToken()
    if (!token) {
      // No auth — show a static message; reducer not needed.
      this.renderUnauthenticated()
      return
    }

    try {
      const res = await fetch('/api/ton-waitlist/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as ApiStatusResponse
      if (data?.success && data.data) {
        this.dispatch({
          type: 'load_ok',
          wantsRealStakes: data.data.wantsRealStakes,
          subscribedAt: data.data.subscribedAt,
        })
      } else {
        this.dispatch({ type: 'load_err' })
      }
    } catch {
      this.dispatch({ type: 'load_err' })
    }
  }

  private async subscribe(): Promise<void> {
    if (this.busy) return
    const token = this.getToken()
    if (!token) return
    this.busy = true
    this.markBusy(true)
    try {
      const res = await fetch('/api/ton-waitlist/subscribe', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as ApiSubscribeResponse
      if (data?.success && data.data?.subscribedAt) {
        this.dispatch({ type: 'subscribe_ok', subscribedAt: data.data.subscribedAt })
      } else {
        this.dispatch({ type: 'action_err' })
      }
    } catch {
      this.dispatch({ type: 'action_err' })
    } finally {
      this.busy = false
      this.markBusy(false)
    }
  }

  private async unsubscribe(): Promise<void> {
    if (this.busy) return
    // window.confirm is intentional — quick, native, follow-up PR can swap for
    // a neon-styled modal once design is finalised.
    if (!window.confirm('Точно не уведомлять?')) return
    const token = this.getToken()
    if (!token) return
    this.busy = true
    this.markBusy(true)
    try {
      const res = await fetch('/api/ton-waitlist/unsubscribe', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: '{}',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as ApiSubscribeResponse
      if (data?.success) {
        this.dispatch({ type: 'unsubscribe_ok' })
      } else {
        this.dispatch({ type: 'action_err' })
      }
    } catch {
      this.dispatch({ type: 'action_err' })
    } finally {
      this.busy = false
      this.markBusy(false)
    }
  }

  private dispatch(action: TonPreviewAction): void {
    this.state = reduceTonPreview(this.state, action)
    this.render()
  }

  private markBusy(busy: boolean): void {
    if (!this.ctaBtn) return
    this.ctaBtn.disabled = busy
    this.ctaBtn.style.opacity = busy ? '0.7' : '1'
  }

  // -----------------------------------------------------------------------
  // Rendering
  // -----------------------------------------------------------------------

  private clear(): void {
    while (this.container.firstChild) this.container.removeChild(this.container.firstChild)
    this.ctaBtn = null
    this.statusLabel = null
    this.waitlistCountEl = null
  }

  private renderUnauthenticated(): void {
    this.clear()
    const wrap = this.makeShell()
    const msg = document.createElement('div')
    msg.style.cssText = 'text-align: center; color: #94a3b8; margin: 24px 0;'
    msg.textContent = 'Войдите в Telegram, чтобы подписаться на анонс TON-режима.'
    wrap.appendChild(msg)
    wrap.appendChild(this.makeReturnButton())
    this.container.appendChild(wrap)
  }

  private render(): void {
    this.clear()
    const wrap = this.makeShell()

    // CTA
    const cta = document.createElement('button')
    cta.type = 'button'
    cta.style.cssText = `
      width: 100%; padding: 14px 18px; margin-top: 20px;
      border-radius: 14px; border: none;
      font-size: 1rem; font-weight: 700; letter-spacing: 0.04em;
      cursor: pointer; font-family: ${FONTS.body};
      transition: filter 0.15s;
    `
    this.ctaBtn = cta

    const status = document.createElement('div')
    status.style.cssText = 'text-align: center; color: #94a3b8; font-size: 0.85rem; margin-top: 12px; min-height: 18px;'
    this.statusLabel = status

    applyCtaState(cta, status, this.state)

    cta.addEventListener('click', () => {
      // Match the button's visible action exactly:
      //   subscribed       → unsubscribe (with confirm)
      //   not_subscribed   → subscribe
      //   error            → retry the original load (NOT subscribe)
      if (this.state.kind === 'subscribed') {
        void this.unsubscribe()
      } else if (this.state.kind === 'not_subscribed') {
        void this.subscribe()
      } else if (this.state.kind === 'error') {
        this.dispatch({ type: 'load' })
        void this.fetchStatus()
      }
    })

    wrap.appendChild(cta)
    wrap.appendChild(status)

    // Explanation (GDD text)
    const explain = document.createElement('div')
    explain.style.cssText = `
      margin-top: 18px; padding: 12px 14px;
      border-radius: 12px; background: rgba(0,2,10,0.55);
      border: 1px solid rgba(255,255,255,0.08);
      color: #cbd5e1; font-size: 0.85rem; text-align: center;
    `
    explain.textContent = '🚧 Мы разрабатываем этот режим. Следи за новостями!'
    wrap.appendChild(explain)

    wrap.appendChild(this.makeReturnButton())
    this.container.appendChild(wrap)
  }

  private makeShell(): HTMLDivElement {
    const wrap = document.createElement('div')
    wrap.style.cssText = 'max-width: 420px; width: 92%; display: flex; flex-direction: column;'

    // Header (title + X)
    const header = document.createElement('div')
    header.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;'

    const title = document.createElement('h2')
    title.style.cssText = `
      ${neonTitleGlow()}
      font-size: clamp(1.4rem, 5.2vw, 1.9rem); margin: 0;
      letter-spacing: 0.02em;
    `
    title.textContent = '💎 РЕЖИМ РЕАЛЬНЫХ СТАВОК'
    header.appendChild(title)

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.style.cssText = `
      background: transparent; border: none; color: #aaa;
      font-size: 1.5rem; cursor: pointer; padding: 4px 8px;
    `
    closeBtn.textContent = 'X'
    closeBtn.addEventListener('click', () => this.requestClose())
    header.appendChild(closeBtn)
    wrap.appendChild(header)

    // Subtitle (GDD)
    const sub = document.createElement('div')
    sub.style.cssText = `
      margin-top: 4px; font-size: 1rem; color: ${NeonColors.textBright};
      letter-spacing: 0.02em;
    `
    sub.textContent = 'Играй на TON. Выигрывай. Выводи.'
    wrap.appendChild(sub)

    // SNAKE-27: social proof line. Always created so any render() restores
    // the cached count via applyCountToEl(). Visibility is toggled by
    // applyCountToEl() based on whether a count has been fetched yet.
    const social = document.createElement('div')
    social.style.cssText = `
      margin-top: 8px; font-size: 0.92rem; color: ${NeonColors.success};
      min-height: 18px; letter-spacing: 0.01em;
    `
    this.waitlistCountEl = social
    wrap.appendChild(social)
    this.applyCountToEl()

    return wrap
  }

  private makeReturnButton(): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.style.cssText = `
      margin-top: 28px; padding: 10px 18px;
      border-radius: 12px; border: 1px solid rgba(255,255,255,0.18);
      background: transparent; color: ${NeonColors.textMuted};
      font-size: 0.9rem; cursor: pointer; font-family: ${FONTS.body};
      align-self: center;
    `
    btn.textContent = '← Вернуться к Рейтингу'
    btn.addEventListener('click', () => this.requestClose())
    return btn
  }

  private requestClose(): void {
    this.onClose?.()
    this.hide()
  }
}

function applyCtaState(
  btn: HTMLButtonElement,
  status: HTMLElement,
  state: TonPreviewState
): void {
  switch (state.kind) {
    case 'loading':
      btn.textContent = 'Загрузка...'
      btn.style.background = 'rgba(255,255,255,0.06)'
      btn.style.color = '#94a3b8'
      btn.disabled = true
      status.textContent = ''
      return
    case 'subscribed':
      btn.textContent = '✓ Вы в списке ожидания'
      btn.style.background = 'rgba(0,255,136,0.12)'
      btn.style.color = NeonColors.success
      btn.style.border = `1px solid ${NeonColors.success}`
      btn.disabled = false
      status.textContent = 'Нажмите чтобы отписаться'
      return
    case 'not_subscribed':
      btn.textContent = '🔔 Уведомить меня'
      btn.style.background = NeonColors.primary
      btn.style.color = '#0f172a'
      btn.style.boxShadow = `0 0 32px ${NeonColors.primaryGlow}`
      btn.disabled = false
      status.textContent = 'Когда режим появится — мы напишем'
      return
    case 'error':
      btn.textContent = 'Не удалось загрузить — попробовать ещё раз'
      btn.style.background = 'rgba(255,51,102,0.12)'
      btn.style.color = NeonColors.danger
      btn.style.border = `1px solid ${NeonColors.danger}`
      btn.disabled = false
      status.textContent = ''
      return
  }
}

